import React, { useEffect, useState } from "react"
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Stack,
  Box,
  Autocomplete,
  Select,
  Skeleton,
  MenuItem,
  InputLabel,
  FormControl,
  SelectChangeEvent,
} from "@mui/material"
import dayjs, { Dayjs } from "dayjs"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo"
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { useTheme } from "@mui/material/styles"
import useMediaQuery from "@mui/material/useMediaQuery"
import {
  addCenter,
  selectCenters,
  updateCenter,
  fetchUsersByName,
  fetchCenterDetails,
  resetData,
  Gatekeeper,
  fetchCenterClusterDetails,
} from "../../slices/centersSlice"

import { Navigate, useNavigate, useParams } from "react-router-dom"
import PageHeader from "../../components/PageHeader/PageHeader"
import ElevatedCard from "../../components/ElevatedCard/ElevatedCard"
import FileUploadDnd from "../../components/FileUploadDnd/FileUploadDnd"
import { CENTERS } from "../../constants/path"
import CommonAutocomplete from "../../components/CommonAutocomplete/CommonAutocomplete"
import { CenterTypes } from "../../constants/data"

const Amenities = [
  "Library",
  "Pet-friendly",
  "Conferencing facilities",
  "Outdoor space",
  "Parking",
  "Lounge",
]

interface FormValues {
  centerName: string
  areaName: string
  googleMapsLocation: string
  contactPersonPhone: string
  dayPassAvailable: boolean
  dayPassPrice: number
  description: string
  rules: string
  gatekeeperIds: Gatekeeper[]
  clusterName: string
  daypassDiscountPercentage: number
  daypassDiscountMessage: string
  bulkDaypassDiscountPercentage: number
  bulkDaypassDiscountMessage: string
  centerCode: string
  centerInvoiceName: string
  centerSequence: string
  centerType: string
}

const AddCenterForm = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { centerId } = useParams()
  const {
    centerDetails,
    centerClusterDetails,
    isCenterDetailsLoading,
    isCenterDataSaved,
    users,
  } = useAppSelector(selectCenters)

  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"))

  // form states
  const [openTime, setOpenTime] = useState<Dayjs | null>(
    dayjs(`2022-04-17T9:00`),
  )
  const [closeTime, setCloseTime] = useState<Dayjs | null>(
    dayjs(`2022-04-17T17:00`),
  )
  const [amenities, setAmenities] = useState<string[]>(
    centerDetails?.amenities || [],
  )
  const [validationErrors, setValidationErrors] = useState({
    contactPersonPhone: "",
  })
  const [formValues, setFormValues] = useState<FormValues>({
    centerName: "",
    areaName: "",
    googleMapsLocation: "",
    contactPersonPhone: "",
    dayPassAvailable: false,
    dayPassPrice: 0,
    description: "",
    rules: "",
    gatekeeperIds: [],
    clusterName: "",
    daypassDiscountPercentage: 0,
    daypassDiscountMessage: "",
    bulkDaypassDiscountPercentage: 0,
    bulkDaypassDiscountMessage: "",
    centerCode: "",
    centerInvoiceName: "",
    centerSequence: "",
    centerType: "",
  })

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [remoteImages, setRemoteImages] = useState<string[]>([])

  // Call the cluster api for displaying cluster dropdown
  useEffect(() => {
    dispatch(fetchCenterClusterDetails())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validateContactPersonPhone = (value: string) => {
    const phoneRegex = /^\d{10}$/ // Regular expression for 10-digit phone number
    if (!phoneRegex.test(value)) {
      return "Invalid phone number (10 digits)"
    }
    return ""
  }

  useEffect(() => {
    if (centerId !== "add")
      dispatch(fetchCenterDetails({ centerId: centerId || "" }))

    return () => {
      dispatch(resetData())
    }
  }, [dispatch, centerId])

  useEffect(() => {
    if (centerDetails.name !== "") {
      setOpenTime(dayjs("2022-04-17T" + centerDetails?.working_hours_start))
      setCloseTime(dayjs("2022-04-17T" + centerDetails?.working_hours_end))
      const validAmenities = centerDetails?.amenities
        ? centerDetails?.amenities.filter((amenity) => amenity !== null)
        : []
      setAmenities(validAmenities)
      setFormValues({
        centerName: centerDetails?.name,
        clusterName: centerDetails?.cluster_name,
        areaName: centerDetails?.address,
        contactPersonPhone: centerDetails?.contact_person_phone || "",
        googleMapsLocation: centerDetails?.google_maps_url,
        dayPassAvailable: centerDetails?.is_day_pass_enabled,
        dayPassPrice: centerDetails?.day_pass_price,
        description: centerDetails?.description || "",
        rules: centerDetails?.rules || "",
        gatekeeperIds:
          (centerDetails?.gatekeeper_details &&
            centerDetails.gatekeeper_details) ||
          [],
        daypassDiscountPercentage:
          centerDetails?.day_pass_discounts_percentage?.["1"]?.value ?? 0,
        daypassDiscountMessage:
          centerDetails?.day_pass_discounts_percentage?.["1"]?.message ?? "",
        bulkDaypassDiscountPercentage:
          centerDetails?.day_pass_discounts_percentage?.["10"]?.value ?? 0,
        bulkDaypassDiscountMessage:
          centerDetails?.day_pass_discounts_percentage?.["10"]?.message ?? "",
        centerCode: centerDetails?.center_code,
        centerInvoiceName: centerDetails?.center_name_for_invoicing,
        centerSequence: centerDetails?.center_sequence,
        centerType: centerDetails?.center_type,
      })
      setRemoteImages(centerDetails?.images)
    }
  }, [centerDetails])

  // input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }))
  }

  const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const numericValue = value.replace(/[^0-9]/g, "")
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: numericValue,
    }))
  }

  const handleClusterDropdownSelect = (e: SelectChangeEvent<string>) => {
    const { value } = e.target
    setFormValues((prevValues) => ({
      ...prevValues,
      clusterName: value,
    }))
  }
  const handleCenterTypeDropdownSelect = (e: SelectChangeEvent<string>) => {
    const { value } = e.target
    setFormValues((prevValues) => ({
      ...prevValues,
      centerType: value,
    }))
  }

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    centerId !== "add"
      ? dispatch(
          updateCenter({
            ...formValues,
            gatekeeperIds: formValues.gatekeeperIds.map(
              (gatekeeper) => gatekeeper.id,
            ),
            amenities,
            contactPersonPhone: validationErrors.contactPersonPhone
              ? ""
              : formValues.contactPersonPhone ?? "",
            openTime: dayjs(openTime).format("HH:mm"),
            closeTime: dayjs(closeTime).format("HH:mm"),
            centerId: centerId || "",
            images: remoteImages,
            newImages: selectedFiles,
            daypassDiscountPercentage: formValues.daypassDiscountPercentage,
            daypassDiscountMessage: formValues.daypassDiscountMessage,
            bulkDaypassDiscountPercentage:
              formValues.bulkDaypassDiscountPercentage,
            bulkDaypassDiscountMessage: formValues.bulkDaypassDiscountMessage,
          }),
        )
      : dispatch(
          addCenter({
            ...formValues,
            gatekeeperIds: formValues.gatekeeperIds.map(
              (gatekeeper) => gatekeeper.id,
            ),
            amenities,
            contactPersonPhone: validationErrors.contactPersonPhone
              ? ""
              : formValues.contactPersonPhone ?? "",
            openTime: dayjs(openTime).format("HH:mm"),
            closeTime: dayjs(closeTime).format("HH:mm"),
            files: selectedFiles,
            daypassDiscountPercentage: formValues.daypassDiscountPercentage,
            daypassDiscountMessage: formValues.daypassDiscountMessage,
            bulkDaypassDiscountPercentage:
              formValues.bulkDaypassDiscountPercentage,
            bulkDaypassDiscountMessage: formValues.bulkDaypassDiscountMessage,
          }),
        )
  }

  return (
    <Box>
      {isCenterDataSaved && (
        <Navigate to={`${CENTERS}/details/${centerDetails.id}`} />
      )}
      <PageHeader
        title={centerId !== "add" ? "Update Center" : "Add Center"}
        breadcrums={[
          { title: "Centers", path: CENTERS },
          { title: centerId !== "add" ? "Add" : "Update" },
        ]}
      />
      <ElevatedCard style={{ width: isSmallScreen ? "84%" : "508px" }}>
        {isCenterDetailsLoading ? (
          <Stack gap={2}>
            <Skeleton variant="rectangular" height={100} />
            <Skeleton variant="rectangular" />
            <Skeleton variant="rectangular" height={50} />
            <Skeleton variant="rectangular" />
            <Skeleton variant="rectangular" />
            <Skeleton variant="rectangular" height={50} />
            <Skeleton variant="rectangular" />
            <Skeleton variant="rectangular" />
            <Skeleton variant="rectangular" height={50} />
            <Skeleton variant="rectangular" />
          </Stack>
        ) : (
          <Stack component="form" gap={2} onSubmit={handleSubmit}>
            <Typography variant="h6">
              {centerId !== "add"
                ? "Update Center Details"
                : "Add Center Details"}
            </Typography>
            <TextField
              name="centerSequence"
              label="Center Number"
              value={formValues.centerSequence}
              onChange={handleNumberInputChange}
              inputProps={{ maxLength: 3, inputMode: "numeric" }}
              required
              size="small"
            />
            <Box sx={{ minWidth: 120 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="center-type-select-label">
                  Center Type*
                </InputLabel>
                <Select
                  required
                  sx={{ maxHeight: "42px" }}
                  labelId="center-type-select-label"
                  id="center-type-select"
                  label="Center type"
                  name="centerType"
                  value={formValues.centerType || ""}
                  onChange={(e) => handleCenterTypeDropdownSelect(e)}
                >
                  {CenterTypes.map((center) => (
                    <MenuItem key={center.value} value={center.value}>
                      {center.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              name="centerName"
              label="Center Name"
              value={formValues.centerName}
              onChange={handleInputChange}
              required
              size="small"
            />
            <TextField
              name="centerInvoiceName"
              label="Center Invoice Name"
              value={formValues.centerInvoiceName}
              onChange={handleInputChange}
              required
              size="small"
            />
            <TextField
              name="centerCode"
              label="Center Code"
              value={formValues.centerCode}
              onChange={handleInputChange}
              required
              size="small"
            />
            {centerDetails?.address && (
              <TextField
                name="areaName"
                label="Address"
                value={formValues.areaName}
                onChange={handleInputChange}
                required
                size="small"
              />
            )}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DemoContainer components={["MobileTimePicker"]}>
                <Stack direction="row" gap={5} justifyContent="space-betweeen">
                  <DemoItem label="Open Time">
                    <MobileTimePicker
                      value={openTime}
                      onChange={(newValue) => setOpenTime(newValue)}
                    />
                  </DemoItem>
                  <DemoItem label="Close Time">
                    <MobileTimePicker
                      value={closeTime}
                      onChange={(newValue) => setCloseTime(newValue)}
                    />
                  </DemoItem>
                </Stack>
              </DemoContainer>
            </LocalizationProvider>
            <Autocomplete
              id="select-amenities"
              options={Amenities}
              value={amenities}
              onChange={(_, newValue) => {
                setAmenities(newValue || [])
              }}
              multiple
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Amenities"
                  variant="outlined"
                  size="small"
                />
              )}
            />
            <CommonAutocomplete
              options={users}
              getOptionLabel={(option) =>
                option
                  ? `${option.mobile} (${option.first_name} ${option.last_name})`
                  : ``
              }
              value={formValues.gatekeeperIds}
              onChange={(newValue) => {
                setFormValues({
                  ...formValues,
                  gatekeeperIds: Array.isArray(newValue)
                    ? newValue
                    : newValue
                    ? [newValue]
                    : [],
                })
              }}
              placeholder="type a name to see option"
              label="Gatekeeper"
              multiple
              fetchOptionsDebounced={(inputValue) => {
                dispatch(fetchUsersByName({ name: inputValue }))
              }}
            />
            <TextField
              name="googleMapsLocation"
              label="Google Maps URL"
              value={formValues.googleMapsLocation}
              onChange={handleInputChange}
              required
              size="small"
            />
            <TextField
              type="number"
              error={!!validationErrors.contactPersonPhone}
              name="centerContactNumber"
              label="Center Contact Number"
              value={formValues.contactPersonPhone}
              onChange={(e) => {
                const error = validateContactPersonPhone(e.target.value)
                setValidationErrors((prevErrors) => ({
                  ...prevErrors,
                  contactPersonPhone: error,
                }))
                setFormValues({
                  ...formValues,
                  contactPersonPhone: e.target.value,
                })
              }}
              size="small"
            />
            {validationErrors.contactPersonPhone && (
              <Typography color="error" fontSize={12} marginTop={-1}>
                {validationErrors.contactPersonPhone}
              </Typography>
            )}

            {/* Cluster Dropdown */}
            <Box sx={{ minWidth: 120 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="demo-simple-select-label">Cluster</InputLabel>
                <Select
                  labelId="cluster-select-label"
                  id="cluster-select"
                  label="Cluster"
                  name="cluster"
                  value={formValues.clusterName || ""}
                  onChange={(e) => handleClusterDropdownSelect(e)}
                >
                  {centerClusterDetails.map((cluster) => (
                    <MenuItem key={cluster.id} value={cluster.name}>
                      {cluster.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Stack
              direction={isSmallScreen ? "column" : "row"}
              gap={isSmallScreen ? 2 : 10}
              justifyContent="space-betweeen"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formValues.dayPassAvailable}
                    onChange={(e) =>
                      setFormValues({
                        ...formValues,
                        dayPassAvailable: e.target.checked,
                      })
                    }
                  />
                }
                label="Day pass availability"
              />
              {formValues.dayPassAvailable && (
                <TextField
                  type="number"
                  name="dayPassPrice"
                  label="Day pass price per day"
                  value={formValues.dayPassPrice}
                  onChange={handleInputChange}
                  required
                  size="small"
                  inputProps={{
                    min: "1",
                  }}
                />
              )}
            </Stack>

            <Stack direction={isSmallScreen ? "column" : "row"} gap={2}>
              <Typography fontWeight={600} width={"100px"}>
                Daypass
              </Typography>
              <TextField
                name="daypassDiscountPercentage"
                label="Discount"
                value={formValues.daypassDiscountPercentage}
                onChange={handleNumberInputChange}
                size="small"
                inputProps={{
                  min: "0",
                  max: "100",
                }}
                sx={{ width: isSmallScreen ? "100%" : "100px" }}
              />
              <TextField
                name="daypassDiscountMessage"
                label="Message"
                value={formValues.daypassDiscountMessage}
                onChange={handleInputChange}
                size="small"
                sx={{ flexGrow: 1 }}
              />
            </Stack>

            <Stack direction={isSmallScreen ? "column" : "row"} gap={2}>
              <Typography fontWeight={600} width={"100px"}>
                BulkDaypass
              </Typography>
              <TextField
                name="bulkDaypassDiscountPercentage"
                label="Discount"
                value={formValues.bulkDaypassDiscountPercentage}
                onChange={handleNumberInputChange}
                size="small"
                inputProps={{
                  min: "0",
                  max: "100",
                }}
                sx={{ width: isSmallScreen ? "100%" : "100px" }}
              />
              <TextField
                name="bulkDaypassDiscountMessage"
                label="Message"
                value={formValues.bulkDaypassDiscountMessage}
                onChange={handleInputChange}
                size="small"
                sx={{ flexGrow: 1 }}
              />
            </Stack>

            <TextField
              multiline
              rows={2}
              name="description"
              label="Description"
              value={formValues.description}
              onChange={handleInputChange}
              size="small"
            />
            <TextField
              multiline
              minRows={4}
              name="rules"
              label="Guidelines"
              value={formValues.rules}
              onChange={handleInputChange}
            />
            <FileUploadDnd
              selectedFiles={selectedFiles}
              setSelectedFiles={setSelectedFiles}
              remoteImages={remoteImages}
              setRemoteImages={setRemoteImages}
            />
            <Stack direction="row" gap={10}>
              <Button
                type="submit"
                sx={{ flexGrow: 1 }}
                variant="contained"
                color="primary"
              >
                Save
              </Button>
              <Button
                sx={{ flexGrow: 1 }}
                onClick={() => navigate(-1)}
                variant="contained"
                color="secondary"
              >
                Back
              </Button>
            </Stack>
          </Stack>
        )}
      </ElevatedCard>
    </Box>
  )
}

export default AddCenterForm
